"use client";

import Link from "next/link";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export type PlanReadOnlyKind = "workspace" | "folder" | "note" | "publish";

const COPY: Record<
  PlanReadOnlyKind,
  { title: string; description: string }
> = {
  workspace: {
    title: "Workspace preserved",
    description:
      "This workspace stays available on your current plan. You can still open and read everything here. Upgrade to continue editing and creating.",
  },
  folder: {
    title: "Folder preserved",
    description:
      "Notes in this folder stay available to read. Upgrade to unlock editing and new notes here.",
  },
  note: {
    title: "Note preserved",
    description:
      "This note stays available on your current plan. Upgrade to continue editing.",
  },
  publish: {
    title: "Publishing paused",
    description:
      "This link is temporarily offline on your current plan. Your note stays in your library. Upgrade to restore publishing.",
  },
};

type Props = {
  open: boolean;
  kind: PlanReadOnlyKind | null;
  onOpenChange: (open: boolean) => void;
};

export function PlanReadOnlyDialog({ open, kind, onOpenChange }: Props) {
  if (!kind) return null;
  const { title, description } = COPY[kind];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="gap-0 overflow-hidden border-border/50 bg-background p-0 shadow-[0_32px_80px_-28px_rgba(0,0,0,0.78)] sm:max-w-[440px]">
        <div className="border-b border-border/40 bg-gradient-to-br from-muted/40 via-background to-background px-8 pb-7 pt-8">
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle className="text-[20px] font-[700] tracking-[-0.03em] text-foreground">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </div>

        <AlertDialogFooter className="gap-2 px-8 pb-8 pt-6 sm:justify-between">
          <AlertDialogCancel className="mt-0 h-11 rounded-xl border-border/70 px-4 text-[13px] font-medium">
            Not now
          </AlertDialogCancel>
          <Button
            asChild
            className="h-11 rounded-xl px-4 text-[13px] font-medium"
          >
            <Link href="/billing">Continue editing with Pro</Link>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
